import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Inbox, RefreshCw, Send, Flag, SkipForward, RotateCcw, Edit3, Paperclip, Trash2, FileText, X,
  ChevronRight, Clock, CheckCircle, AlertTriangle, Mail, Loader2, Zap, Sparkles, UserCheck, CalendarClock,
  Ban, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../lib/api';
import { usePendingSends } from '../context/PendingSendContext';
import SenderRulesDialog from './inbox/SenderRulesDialog';
import SkipSenderDialog from './inbox/SkipSenderDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;
const ACCEPTED_ATTACHMENT_TYPES = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png';

function toLocalInputValue(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-[#E5F6F6] text-[#008383]', icon: Mail },
  drafted: { label: 'Draft Ready', color: 'bg-[#E6EFE9] text-[#3D5A45]', icon: Edit3 },
  sent: { label: 'Sent', color: 'bg-[#E0F2F5] text-[#109AB4]', icon: CheckCircle },
  flagged: { label: 'Flagged', color: 'bg-[#FCEEEA] text-[#A3503B]', icon: AlertTriangle },
  skipped: { label: 'Skipped', color: 'bg-[#F0EFEA] text-[#5C5A56]', icon: SkipForward },
  scheduled: { label: 'Scheduled', color: 'bg-[#FBF1DC] text-[#8A6D3B]', icon: Clock },
  pending_send: { label: 'Undo available', color: 'bg-[#E1F1EE] text-[#176C62]', icon: Clock },
};

const CLASS_CONFIG = {
  new_enquiry: { label: 'New Enquiry', color: '#109AB4' },
  pricing: { label: 'Pricing', color: '#4E8C9A' },
  booking: { label: 'Booking', color: '#0E8FA8' },
  status_update: { label: 'Status Update', color: '#B98A4B' },
  keepsakes_urns: { label: 'Keepsakes & Urns', color: '#B07C8A' },
  vet_clinic: { label: 'Vet Clinic', color: '#6E9B7C' },
  business_admin: { label: 'Business Admin', color: '#4E6571' },
  tree_planting: { label: 'Tree Planting', color: '#7C9A5B' },
  general_question: { label: 'General', color: '#7A7A75' },
  complaint: { label: 'Complaint', color: '#ef4444' },
  sensitive: { label: 'Sensitive', color: '#f97316' },
  spam: { label: 'Spam', color: '#9ca3af' },
  automated: { label: 'Automated', color: '#66756B' },
  internal: { label: 'Internal', color: '#9ca3af' },
  error: { label: 'Error', color: '#ef4444' },
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  } catch {
    return dateStr;
  }
}

function formatLastChecked(secondsAgo) {
  if (secondsAgo < 5) return 'just now';
  if (secondsAgo < 60) return `${secondsAgo}s ago`;
  const mins = Math.floor(secondsAgo / 60);
  return `${mins}m ago`;
}

function formatFileSize(bytes) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

const ClassBadge = ({ classification }) => {
  const config = CLASS_CONFIG[classification] || CLASS_CONFIG.general_question;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
      style={{ backgroundColor: config.color }}
    >
      {config.label}
    </span>
  );
};

const REASON_LABELS = {
  staff_bulk_skip: 'Skipped by staff',
  staff_skip: 'Skipped by staff',
  sender_rule: 'Always-skip sender rule',
  verified_automated_or_marketing: 'Verified automated message',
};

const ReasonBadge = ({ email }) => {
  const reason = email.actionability_reason || REASON_LABELS[email.skip_reason];
  if (!reason) return null;
  const tone = email.status === 'flagged'
    ? 'bg-[#FBF1DC] text-[#8A6D3B] border-[#EAD7AE]'
    : email.status === 'skipped'
      ? 'bg-[#F0EFEA] text-[#5C5A56] border-[#DCD9D1]'
      : 'bg-[#E1F1EE] text-[#176C62] border-[#B8D8D1]';
  return (
    <span className={`inline-flex max-w-full px-2 py-0.5 rounded-full border text-[10px] font-semibold truncate ${tone}`} title={reason} data-testid={`badge-reason-${email.id}`}>
      {reason}
    </span>
  );
};

const InboxAssistant = () => {
  const { queuePendingSend } = usePendingSends();
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [sending, setSending] = useState(false);
  const [editText, setEditText] = useState('');
  const [savingDraft, setSavingDraft] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const fileInputRef = useRef(null);
  const dragDepthRef = useRef(0);
  const [previewTab, setPreviewTab] = useState('original');
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('needs_action');
  const [inboxFilter, setInboxFilter] = useState('info');
  const [signatures, setSignatures] = useState([]);
  const [selectedSignature, setSelectedSignature] = useState('linda_serrano');
  const [customName, setCustomName] = useState('');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [automation, setAutomation] = useState(null);
  const prevCountRef = useRef(null);
  const [secondsAgo, setSecondsAgo] = useState(0);
  const [refineInput, setRefineInput] = useState('');
  const [refining, setRefining] = useState(false);
  const [selectedEmailIds, setSelectedEmailIds] = useState([]);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkWorking, setBulkWorking] = useState(false);
  const [senderRulesOpen, setSenderRulesOpen] = useState(false);
  const [senderRules, setSenderRules] = useState([]);
  const [senderRulesLoading, setSenderRulesLoading] = useState(false);
  const [skipSenderOpen, setSkipSenderOpen] = useState(false);
  const [skipSenderWorking, setSkipSenderWorking] = useState(false);

  const loadEmails = useCallback(async () => {
    try {
      const res = await apiFetch(`${BACKEND_URL}/api/inbox`);
      const data = await res.json();
      setEmails(data);
    } catch (e) {
      console.error('Failed to load emails:', e);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const res = await apiFetch(`${BACKEND_URL}/api/inbox/stats`);
      const data = await res.json();
      setStats(data);
    } catch (e) {
      console.error('Failed to load stats:', e);
    }
  }, []);

  const loadAutomation = useCallback(async () => {
    try {
      const res = await apiFetch(`${BACKEND_URL}/api/automation/status`);
      const data = await res.json();
      setAutomation(data);
    } catch (e) {
      // stay silent
    }
  }, []);

  const loadSenderRules = useCallback(async () => {
    setSenderRulesLoading(true);
    try {
      const response = await apiFetch(`${BACKEND_URL}/api/inbox-rules`);
      const data = await response.json();
      setSenderRules(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load sender rules:', error);
    }
    setSenderRulesLoading(false);
  }, []);

  useEffect(() => {
    loadEmails();
    loadStats();
    loadAutomation();
    loadSenderRules();
    // Load signatures
    apiFetch(`${BACKEND_URL}/api/signatures`)
      .then(res => res.json())
      .then(data => setSignatures(data))
      .catch(() => {});
  }, [loadEmails, loadStats, loadAutomation, loadSenderRules]);

  const handleCreateSenderRule = async () => {
    if (!selectedEmail || skipSenderWorking) return;
    setSkipSenderWorking(true);
    try {
      const response = await apiFetch(`${BACKEND_URL}/api/inbox-rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email_id: selectedEmail.id }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Could not create sender rule');
      setSkipSenderOpen(false);
      setSelectedEmail(null);
      await Promise.all([loadEmails(), loadStats(), loadSenderRules()]);
      toast.success(`Future emails from ${data.sender_email} will skip Needs Action`);
    } catch (error) {
      toast.error(error.message || 'Could not create sender rule');
    }
    setSkipSenderWorking(false);
  };

  const handleRemoveSenderRule = async (rule) => {
    try {
      const response = await apiFetch(`${BACKEND_URL}/api/inbox-rules/${rule.id}`, { method: 'DELETE' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Could not remove sender rule');
      await loadSenderRules();
      toast.success(`Removed rule for ${rule.sender_email}`);
    } catch (error) {
      toast.error(error.message || 'Could not remove sender rule');
    }
  };

  useEffect(() => {
    setSelectedEmailIds([]);
    setBulkConfirmOpen(false);
  }, [filter, inboxFilter]);

  useEffect(() => {
    const handleUndone = (event) => {
      const emailId = event.detail?.inbox_email_id;
      if (emailId && selectedEmail?.id === emailId) {
        setSelectedEmail(previous => ({ ...previous, status: 'drafted' }));
      }
      loadEmails();
      loadStats();
    };
    const handleSettled = (event) => {
      const emailId = event.detail?.inbox_email_id;
      if (emailId && selectedEmail?.id === emailId) {
        setSelectedEmail(previous => ({
          ...previous,
          status: event.detail?.status === 'sent' ? 'sent' : 'drafted',
          sent_at: event.detail?.smtp_accepted_at || previous.sent_at,
          smtp_status: event.detail?.status === 'sent' ? 'accepted' : previous.smtp_status,
          outbound_message_id: event.detail?.message_id || previous.outbound_message_id,
          sender_email: event.detail?.sender_email || previous.sender_email,
          sent_folder_status: event.detail?.sent_folder_status || previous.sent_folder_status,
        }));
      }
      loadEmails();
      loadStats();
    };
    window.addEventListener('ps-send-undone', handleUndone);
    window.addEventListener('ps-send-settled', handleSettled);
    return () => {
      window.removeEventListener('ps-send-undone', handleUndone);
      window.removeEventListener('ps-send-settled', handleSettled);
    };
  }, [loadEmails, loadStats, selectedEmail?.id]);

  const handleFetchNew = async () => {
    setFetching(true);
    try {
      const res = await apiFetch(`${BACKEND_URL}/api/inbox/fetch`, { method: 'POST' });
      const data = await res.json();
      if (data.added > 0) {
        toast.success(`Added ${data.added}: ${data.new_drafts || 0} drafts, ${data.flagged || 0} flagged, ${data.skipped || 0} skipped`);
      } else if (data.fetched > 0) {
        toast.info(`${data.fetched} email(s) found, but all already processed`);
      } else {
        toast.info('No new emails in the inbox');
      }
      await loadEmails();
      await loadStats();
      await loadAutomation();
    } catch (e) {
      toast.error('Failed to fetch emails');
    }
    setFetching(false);
  };

  // Light background refresh: the server scheduler does the heavy IMAP fetch +
  // drafting; the UI just reloads the list/stats and surfaces new drafts.
  const refreshInbox = useCallback(async () => {
    try {
      const [emailsRes, statsRes, autoRes] = await Promise.all([
        apiFetch(`${BACKEND_URL}/api/inbox`),
        apiFetch(`${BACKEND_URL}/api/inbox/stats`),
        apiFetch(`${BACKEND_URL}/api/automation/status`),
      ]);
      const emailsData = await emailsRes.json();
      const statsData = await statsRes.json();
      const autoData = await autoRes.json();
      if (prevCountRef.current !== null && emailsData.length > prevCountRef.current) {
        toast.success(`${emailsData.length - prevCountRef.current} new email(s) added`);
      }
      prevCountRef.current = emailsData.length;
      setEmails(emailsData);
      setStats(statsData);
      setAutomation(autoData);
    } catch (e) {
      // stay silent on background errors
    }
  }, []);

  // Poll the server every 30s to surface drafts created by the 24/7 scheduler
  useEffect(() => {
    const id = setInterval(() => { refreshInbox(); }, 30000);
    return () => clearInterval(id);
  }, [refreshInbox]);

  const toggleAutomation = async () => {
    const next = !(automation?.enabled);
    try {
      const res = await apiFetch(`${BACKEND_URL}/api/automation/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next }),
      });
      const data = await res.json();
      setAutomation(data);
      toast.info(next ? '24/7 auto-drafting resumed' : 'Auto-drafting paused');
    } catch (e) {
      toast.error('Could not update automation');
    }
  };

  // Tick the "checked Xs ago" indicator from the server's last run time
  useEffect(() => {
    const lastRun = automation?.last_run;
    if (!lastRun) return;
    const lastMs = new Date(lastRun).getTime();
    const update = () => setSecondsAgo(Math.max(0, Math.floor((Date.now() - lastMs) / 1000)));
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [automation?.last_run]);

  const handleSelectEmail = async (emailItem) => {
    setPreviewTab('original');
    setIsDraggingFiles(false);
    setSelectedEmail(emailItem);
    setEditText(emailItem.draft_text || '');
    setAttachments([]);
    setRefineInput('');
    // Default the signature based on which inbox the email arrived into (staff can still switch)
    const mailbox = (emailItem.mailbox || '').toLowerCase();
    const defaultSig = emailItem.signature_id || (
      mailbox.startsWith('admin@') ? 'linda_serrano_admin' :
      mailbox.startsWith('giacomo@') ? 'giacomo_pareja' :
      'linda_serrano'
    );
    setSelectedSignature(defaultSig);
    setCustomName('');
    try {
      const response = await apiFetch(`${BACKEND_URL}/api/inbox/${emailItem.id}/attachments`);
      if (response.ok) setAttachments(await response.json());
    } catch (e) {
      toast.error('Could not load attachments');
    }
  };

  const handleRefine = async () => {
    if (!selectedEmail || !refineInput.trim() || refining) return;
    setRefining(true);
    try {
      const res = await apiFetch(`${BACKEND_URL}/api/inbox/${selectedEmail.id}/refine`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instruction: refineInput.trim(), current_draft: editText, signature_id: selectedSignature, custom_name: customName || undefined }),
      });
      const data = await res.json();
      if (data.success) {
        setSelectedEmail(prev => ({
          ...prev,
          draft_text: data.draft_text,
          draft_html: data.draft_html,
          refine_history: data.refine_history,
          status: 'drafted',
        }));
        setEditText(data.draft_text);
        setRefineInput('');
        toast.success('Draft refined');
        await loadEmails();
      } else {
        toast.error('Could not refine draft');
      }
    } catch (e) {
      toast.error('Failed to refine draft');
    }
    setRefining(false);
  };

  const handleRegenerate = async () => {
    if (!selectedEmail) return;
    setLoading(true);
    try {
      const res = await apiFetch(`${BACKEND_URL}/api/inbox/${selectedEmail.id}/regenerate`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setSelectedEmail(prev => ({
          ...prev,
          draft_text: data.draft_text,
          draft_html: data.draft_html,
          classification: data.classification || prev.classification,
          confidence: data.confidence || prev.confidence,
          no_reply_needed: false,
          actionability: 'reply_required',
          actionability_reason: 'Staff generated a reply draft',
          triage_source: 'staff',
          status: 'drafted',
        }));
        setEditText(data.draft_text);
        toast.success('Draft regenerated');
        await loadEmails();
      }
    } catch (e) {
      toast.error('Failed to regenerate draft');
    }
    setLoading(false);
  };

  const handleSaveDraft = async () => {
    if (!selectedEmail) return;
    setSavingDraft(true);
    try {
      const res = await apiFetch(`${BACKEND_URL}/api/inbox/${selectedEmail.id}/draft`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ draft_text: editText, signature_id: selectedSignature, custom_name: customName || undefined }),
      });
      const data = await res.json();
      if (data.success) {
        setSelectedEmail(prev => ({ ...prev, draft_text: editText, draft_html: data.draft_html, status: 'drafted' }));
        toast.success('Draft updated');
        await loadEmails();
      }
    } catch (e) {
      toast.error('Failed to save draft');
    }
    setSavingDraft(false);
  };

  const uploadAttachmentFiles = async (files) => {
    if (!selectedEmail || files.length === 0 || uploadingAttachments) return;
    if (attachments.length + files.length > 10) {
      toast.error('You can attach up to 10 files');
      return;
    }
    const oversized = files.find(file => file.size > MAX_ATTACHMENT_SIZE);
    if (oversized) {
      toast.error(`${oversized.name} exceeds the 10 MB limit`);
      return;
    }
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    setUploadingAttachments(true);
    try {
      const response = await apiFetch(`${BACKEND_URL}/api/inbox/${selectedEmail.id}/attachments`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Upload failed');
      setAttachments(current => [...current, ...data.attachments]);
      toast.success(`${data.attachments.length} attachment${data.attachments.length === 1 ? '' : 's'} added`);
    } catch (error) {
      toast.error(error.message || 'Could not upload attachment');
    }
    setUploadingAttachments(false);
  };

  const handleAttachmentUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    await uploadAttachmentFiles(files);
  };

  const hasDraggedFiles = (event) => Array.from(event.dataTransfer?.types || []).includes('Files');

  const handleReplyDragEnter = (event) => {
    if (!hasDraggedFiles(event) || !selectedEmail || ['sent', 'pending_send'].includes(selectedEmail.status) || selectedEmail.no_reply_needed || uploadingAttachments) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    setIsDraggingFiles(true);
  };

  const handleReplyDragOver = (event) => {
    if (!hasDraggedFiles(event) || !selectedEmail || ['sent', 'pending_send'].includes(selectedEmail.status) || selectedEmail.no_reply_needed || uploadingAttachments) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleReplyDragLeave = (event) => {
    if (!isDraggingFiles) return;
    event.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDraggingFiles(false);
  };

  const handleReplyDrop = async (event) => {
    if (!hasDraggedFiles(event) || !selectedEmail || ['sent', 'pending_send'].includes(selectedEmail.status) || selectedEmail.no_reply_needed || uploadingAttachments) return;
    event.preventDefault();
    dragDepthRef.current = 0;
    setIsDraggingFiles(false);
    await uploadAttachmentFiles(Array.from(event.dataTransfer.files || []));
  };

  const handleRemoveAttachment = async (attachmentId) => {
    if (!selectedEmail) return;
    try {
      const response = await apiFetch(`${BACKEND_URL}/api/inbox/${selectedEmail.id}/attachments/${attachmentId}`, { method: 'DELETE' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Remove failed');
      setAttachments(current => current.filter(item => item.id !== attachmentId));
      toast.success('Attachment removed');
    } catch (error) {
      toast.error(error.message || 'Could not remove attachment');
    }
  };

  const handleApplySignature = async (sigId, name) => {
    if (!selectedEmail || !selectedEmail.draft_text) return;
    try {
      const res = await apiFetch(`${BACKEND_URL}/api/inbox/${selectedEmail.id}/draft`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ draft_text: selectedEmail.draft_text, signature_id: sigId, custom_name: name || undefined }),
      });
      const data = await res.json();
      if (data.success) {
        setSelectedEmail(prev => ({ ...prev, draft_html: data.draft_html }));
        toast.success('Signature updated');
      }
    } catch (e) {
      toast.error('Failed to update signature');
    }
  };

  const handleSendDraft = async (sendAt = null) => {
    if (!selectedEmail) return;
    setSending(true);
    try {
      const res = await apiFetch(`${BACKEND_URL}/api/inbox/${selectedEmail.id}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draft_text: editText,
          signature_id: selectedSignature,
          custom_name: customName || undefined,
          send_at: sendAt || undefined,
          attachment_ids: attachments.map(item => item.id),
        }),
      });
      const data = await res.json();
      if (data.success && data.scheduled) {
        setSelectedEmail(prev => ({ ...prev, status: 'scheduled' }));
        toast.success(`Reply scheduled for ${new Date(data.send_at).toLocaleString()}`);
        setScheduleOpen(false);
        setScheduleAt('');
        await loadEmails();
        await loadStats();
      } else if (data.success && data.queued) {
        queuePendingSend({
          id: data.id,
          inbox_email_id: selectedEmail.id,
          recipients: [data.sent_to],
          subject: selectedEmail.subject,
          send_at: data.send_at,
          undo_until: data.undo_until,
          status: data.status || 'pending_undo',
        });
        setSelectedEmail(prev => ({ ...prev, status: 'pending_send' }));
        toast.message('Reply queued. You have 10 seconds to undo.');
        await loadEmails();
        await loadStats();
      } else if (data.success) {
        setSelectedEmail(prev => ({ ...prev, status: 'sent' }));
        toast.success(`SMTP accepted the reply to ${data.sent_to}`);
        await loadEmails();
        await loadStats();
      }
    } catch (e) {
      toast.error('Failed to send reply');
    }
    setSending(false);
  };

  const handleFlag = async () => {
    if (!selectedEmail) return;
    try {
      await apiFetch(`${BACKEND_URL}/api/inbox/${selectedEmail.id}/flag`, { method: 'POST' });
      setSelectedEmail(prev => ({
        ...prev,
        status: 'flagged',
        no_reply_needed: true,
        actionability: 'review_only',
        actionability_reason: 'Flagged by staff for review',
        triage_source: 'staff',
      }));
      toast.info('Email flagged for human review');
      await loadEmails();
      await loadStats();
    } catch (e) {
      toast.error('Failed to flag email');
    }
  };

  const runBulkAction = async (action, emailIds, offerUndo = true) => {
    if (!emailIds.length || bulkWorking) return;
    setBulkWorking(true);
    try {
      const response = await apiFetch(`${BACKEND_URL}/api/inbox/bulk-${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email_ids: emailIds }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || `Could not ${action} emails`);
      const affectedIds = data.email_ids || [];
      setSelectedEmailIds([]);
      setBulkConfirmOpen(false);
      if (selectedEmail && affectedIds.includes(selectedEmail.id)) setSelectedEmail(null);
      await Promise.all([loadEmails(), loadStats()]);
      const label = action === 'skip' ? 'skipped' : 'restored to Needs Action';
      const toastOptions = offerUndo && affectedIds.length > 0 ? {
        action: {
          label: 'Undo',
          onClick: () => runBulkAction(action === 'skip' ? 'restore' : 'skip', affectedIds, false),
        },
      } : undefined;
      toast.success(`${data.changed_count} email${data.changed_count === 1 ? '' : 's'} ${label}`, toastOptions);
    } catch (error) {
      toast.error(error.message || `Failed to ${action} emails`);
    }
    setBulkWorking(false);
  };

  const handleSkip = async () => {
    if (!selectedEmail) return;
    const action = selectedEmail.status === 'skipped' ? 'restore' : 'skip';
    await runBulkAction(action, [selectedEmail.id]);
  };

  const filteredEmails = emails.filter(e => {
    // Inbox filter
    if (inboxFilter !== 'all') {
      if (!e.mailbox || !e.mailbox.startsWith(inboxFilter)) return false;
    }
    // Status filter
    if (filter === 'all') return true;
    if (filter === 'needs_action') return ['drafted', 'new', 'pending_send'].includes(e.status);
    return e.status === filter;
  });

  const bulkMode = filter === 'skipped' ? 'restore' : 'skip';
  const selectableEmails = filteredEmails.filter(emailItem => (
    bulkMode === 'restore'
      ? emailItem.status === 'skipped'
      : ['new', 'drafted', 'flagged'].includes(emailItem.status)
  ));
  const selectableIds = selectableEmails.map(emailItem => emailItem.id);
  const selectedVisibleIds = selectedEmailIds.filter(emailId => selectableIds.includes(emailId));
  const allVisibleSelected = selectableIds.length > 0 && selectedVisibleIds.length === selectableIds.length;
  const someVisibleSelected = selectedVisibleIds.length > 0 && !allVisibleSelected;

  const toggleVisibleSelection = (checked) => {
    setSelectedEmailIds(checked ? selectableIds : []);
  };

  const toggleEmailSelection = (emailId, checked) => {
    setSelectedEmailIds(current => (
      checked
        ? Array.from(new Set([...current, emailId]))
        : current.filter(id => id !== emailId)
    ));
  };

  const selectedSenderRule = selectedEmail && senderRules.find(rule => rule.sender_email === (selectedEmail.from_email || '').toLowerCase());

  return (
    <div className="h-[calc(100vh-69px)] flex" data-testid="inbox-assistant">
      {/* Left Panel — Email List */}
      <div className={`${selectedEmail ? 'hidden md:flex' : 'flex'} w-full md:w-[400px] border-r border-border flex-col bg-card`}>
        {/* Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-2xl font-serif font-medium text-foreground flex items-center gap-2">
              <Inbox className="w-5 h-5 text-primary" />
              AI Inbox
            </h2>
            <Button
              size="sm"
              onClick={handleFetchNew}
              disabled={fetching}
              data-testid="fetch-emails-btn"
              className="bg-primary hover:bg-accent text-primary-foreground rounded-full shadow-sm"
            >
              {fetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              <span className="ml-1.5">{fetching ? 'Checking...' : 'Check Mail'}</span>
            </Button>
          </div>

          {/* Stats */}
          {stats && (
            <div className="flex gap-2 text-xs">
              <span className="px-2.5 py-1 rounded-full bg-[#E6EFE9] text-[#3D5A45] font-semibold">{stats.drafted} Drafts</span>
              <span className="px-2.5 py-1 rounded-full bg-[#E0F2F5] text-[#109AB4] font-semibold">{stats.sent} Sent</span>
              <span className="px-2.5 py-1 rounded-full bg-[#FCEEEA] text-[#A3503B] font-semibold">{stats.flagged} Flagged</span>
            </div>
          )}

          {/* Server-side automation control */}
          <div className="flex items-center justify-between mt-3 flex-wrap gap-2" data-testid="auto-refresh-row">
            <button
              type="button"
              onClick={toggleAutomation}
              data-testid="auto-refresh-toggle"
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                automation?.enabled
                  ? 'bg-[#E0F2F5] text-primary hover:bg-[#D0EBEF]'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
              title={automation?.enabled
                ? `24/7 auto-drafting is on — the server checks every ${Math.round((automation?.interval_seconds || 120) / 60)} min, even when this app is closed. Click to pause.`
                : 'Auto-drafting is paused. Click to resume 24/7 monitoring.'}
            >
              <Zap className={`w-3.5 h-3.5 ${automation?.enabled ? 'text-primary' : 'text-muted-foreground'}`} />
              24/7 Auto-drafting: {automation?.enabled ? 'On' : 'Off'}
              {automation?.enabled && <span className="relative flex h-2 w-2 ml-0.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span></span>}
            </button>
            {automation?.last_run && (
              <span className="text-xs text-muted-foreground flex items-center gap-1" data-testid="last-checked-label">
                <Clock className="w-3 h-3" /> Server checked {formatLastChecked(secondsAgo)}
              </span>
            )}
          </div>

          {/* Inbox Category Tabs */}
          <div className="flex border-b border-border overflow-x-auto">
            {[
              { key: 'all', label: 'All Inboxes' },
              { key: 'info', label: 'info@' },
              { key: 'admin', label: 'admin@' },
              { key: 'giacomo', label: 'giacomo@' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setInboxFilter(tab.key)}
                data-testid={`inbox-mailbox-filter-${tab.key}`}
                className={`flex-shrink-0 sm:flex-1 px-3 py-2.5 text-xs font-semibold whitespace-nowrap transition-colors border-b-2 ${
                  inboxFilter === tab.key
                    ? 'border-primary text-primary bg-[#E0F2F5]/40'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/30'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Status Filter */}
          <div className="flex gap-1 mt-3 flex-wrap">
            {[
              { key: 'all', label: 'All' },
              { key: 'needs_action', label: 'Needs Action' },
              { key: 'sent', label: 'Sent' },
              { key: 'flagged', label: 'Flagged' },
              { key: 'skipped', label: 'Skipped' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`inbox-status-filter-${f.key}`}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                  filter === f.key
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {selectableEmails.length > 0 && (
          <div className="border-b border-border bg-[#F7FAF9] px-3 py-2.5 flex items-center justify-between gap-3" data-testid="bulk-selection-toolbar">
            <label className="flex items-center gap-2 text-xs font-semibold text-foreground cursor-pointer min-w-0">
              <Checkbox
                checked={allVisibleSelected ? true : someVisibleSelected ? 'indeterminate' : false}
                onCheckedChange={toggleVisibleSelection}
                aria-label="Select all visible eligible emails"
                data-testid="select-all-visible-emails-checkbox"
              />
              <span className="truncate">Select visible</span>
              {selectedVisibleIds.length > 0 && (
                <span className="rounded-full bg-primary px-2 py-0.5 text-[11px] text-primary-foreground" data-testid="bulk-selected-count">
                  {selectedVisibleIds.length}
                </span>
              )}
            </label>
            <Button
              size="sm"
              variant={bulkMode === 'restore' ? 'outline' : 'default'}
              className="rounded-full h-8 flex-shrink-0"
              disabled={selectedVisibleIds.length === 0 || bulkWorking}
              onClick={() => setBulkConfirmOpen(true)}
              data-testid={bulkMode === 'restore' ? 'bulk-restore-selected-button' : 'bulk-skip-selected-button'}
            >
              {bulkWorking ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : bulkMode === 'restore' ? <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> : <SkipForward className="w-3.5 h-3.5 mr-1.5" />}
              {bulkMode === 'restore' ? 'Restore' : 'Skip'}
            </Button>
          </div>
        )}

        {/* Email List */}
        <div className="flex-1 overflow-y-auto">
          {filteredEmails.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground flex flex-col items-center justify-center h-full">
              <div className="w-14 h-14 rounded-full bg-[#E0F2F5] flex items-center justify-center mb-4">
                <Inbox className="w-7 h-7 text-primary opacity-70" />
              </div>
              <p className="text-base font-serif text-foreground">Your inbox is peaceful right now</p>
              <p className="text-xs mt-1.5 max-w-[220px]">New customer emails will appear here automatically, or click &ldquo;Check Mail&rdquo; to look now.</p>
            </div>
          ) : (
            filteredEmails.map(emailItem => {
              const isSelectable = selectableIds.includes(emailItem.id);
              return (
              <div
                key={emailItem.id}
                className={`flex items-stretch border-b border-border transition-colors ${
                  selectedEmail?.id === emailItem.id ? 'bg-[#E0F2F5]/50 border-l-2 border-l-primary' : 'hover:bg-[#F0EFEA]/60'
                }`}
              >
                {isSelectable && (
                  <div className="flex items-start pl-3 pt-5">
                    <Checkbox
                      checked={selectedEmailIds.includes(emailItem.id)}
                      onCheckedChange={checked => toggleEmailSelection(emailItem.id, checked === true)}
                      aria-label={`Select ${emailItem.subject || 'email'} from ${emailItem.from_name || emailItem.from_email}`}
                      data-testid={`select-email-checkbox-${emailItem.id}`}
                    />
                  </div>
                )}
                <button
                  onClick={() => handleSelectEmail(emailItem)}
                  data-testid={`email-item-${emailItem.id}`}
                  className="flex-1 min-w-0 text-left p-4"
                >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm text-foreground truncate">
                        {emailItem.from_name || emailItem.from_email}
                      </span>
                      <span className="text-xs text-muted-foreground flex-shrink-0">
                        {formatDate(emailItem.date || emailItem.created_at)}
                      </span>
                    </div>
                    <p className="text-sm text-foreground/80 truncate mb-1.5">{emailItem.subject || '(no subject)'}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusBadge status={emailItem.status} />
                      {emailItem.classification && <ClassBadge classification={emailItem.classification} />}
                      {emailItem.mailbox && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#E0F2F5] text-[#0E7E92] font-semibold">
                          {emailItem.mailbox.split('@')[0]}@
                        </span>
                      )}
                      {emailItem.crm_match?.matched && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#E6EFE9] text-[#3D5A45] font-semibold inline-flex items-center gap-0.5" data-testid={`crm-badge-${emailItem.id}`} title="Matched to a CRM customer record">
                          <UserCheck className="w-2.5 h-2.5" /> CRM
                        </span>
                      )}
                      <ReasonBadge email={emailItem} />
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" />
                </div>
                </button>
              </div>
              );
            })
          )}
        </div>
      </div>

      {/* Right Panel — Email Detail + Draft */}
      <div
        className={`${selectedEmail ? 'flex' : 'hidden md:flex'} relative flex-1 flex-col bg-background overflow-hidden`}
        onDragEnter={previewTab === 'draft' ? handleReplyDragEnter : undefined}
        onDragOver={previewTab === 'draft' ? handleReplyDragOver : undefined}
        onDragLeave={previewTab === 'draft' ? handleReplyDragLeave : undefined}
        onDrop={previewTab === 'draft' ? handleReplyDrop : undefined}
        data-testid="reply-workspace-drop-zone"
      >
        {isDraggingFiles && (
          <div className="absolute inset-3 z-[90] pointer-events-none rounded-lg border-2 border-dashed border-primary bg-white/95 flex items-center justify-center shadow-xl" data-testid="reply-drop-overlay">
            <div className="text-center px-6">
              <div className="w-14 h-14 rounded-full bg-[#E0F2F5] flex items-center justify-center mx-auto mb-3">
                <Paperclip className="w-6 h-6 text-primary" />
              </div>
              <p className="text-lg font-serif text-foreground">Drop files to attach</p>
              <p className="text-sm text-muted-foreground mt-1">PDF, Word, Excel, JPG or PNG, up to 10 MB each</p>
            </div>
          </div>
        )}
        {!selectedEmail ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#E0F2F5] flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-primary opacity-60" />
              </div>
              <p className="text-lg font-serif text-foreground">Select an email to begin</p>
              <p className="text-sm mt-1">Choose a message on the left to review its AI draft.</p>
            </div>
          </div>
        ) : (
          <>
            {/* Email Header */}
            <div className="p-4 sm:p-5 border-b border-border bg-card">
              {/* Mobile back button */}
              <button
                onClick={() => setSelectedEmail(null)}
                data-testid="back-to-inbox-btn"
                className="md:hidden inline-flex items-center gap-1.5 mb-3 text-sm font-semibold text-primary hover:text-accent transition-colors"
              >
                <ChevronRight className="w-4 h-4 rotate-180" /> Back to inbox
              </button>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="text-lg sm:text-xl font-serif font-medium text-foreground mb-1 break-words">{selectedEmail.subject || '(no subject)'}</h3>
                  <p className="text-sm text-muted-foreground">
                    From: <span className="text-foreground">{selectedEmail.from_name}</span> &lt;{selectedEmail.from_email}&gt;
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    To: {selectedEmail.to} &middot; {selectedEmail.date}
                  </p>
                  {selectedEmail.status === 'sent' && selectedEmail.sent_at && (
                    <div className="mt-2 inline-flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-[#B8D8D1] bg-[#E1F1EE] px-3 py-2 text-xs text-[#176C62]" data-testid="reply-delivery-confirmation">
                      <CheckCircle className="w-3.5 h-3.5" />
                      <strong>SMTP accepted {new Date(selectedEmail.sent_at).toLocaleString('en-AU')}</strong>
                      <span>from {selectedEmail.sender_email || selectedEmail.mailbox}</span>
                      <span>{selectedEmail.sent_folder_status === 'archived' ? '· saved in Sent' : '· Sent-folder copy unavailable'}</span>
                    </div>
                  )}
                  {selectedEmail.crm_match?.matched && (
                    <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E6EFE9] text-[#3D5A45] text-xs font-semibold" data-testid="crm-match-badge">
                      <UserCheck className="w-3.5 h-3.5" />
                      <span>
                        CRM matched: {selectedEmail.crm_match.first_name || selectedEmail.crm_match.customer_name}
                        {selectedEmail.crm_match.total_pets > 1 ? ` · ${selectedEmail.crm_match.total_pets} pets` : ''}
                        {selectedEmail.crm_match.primary_pet ? ` · re: ${selectedEmail.crm_match.primary_pet}` : ''}
                      </span>
                    </div>
                  )}
                  <div className="mt-2"><ReasonBadge email={selectedEmail} /></div>
                </div>
                <div className="flex flex-col sm:flex-row items-end sm:items-center gap-1.5 sm:gap-2 flex-shrink-0">
                  <StatusBadge status={selectedEmail.status} />
                  {selectedEmail.classification && <ClassBadge classification={selectedEmail.classification} />}
                  {selectedEmail.status === 'skipped' && (
                    <div className="flex items-center gap-1.5 mt-1 sm:mt-0">
                      <Button size="sm" variant="outline" className="rounded-full h-8" onClick={handleSkip} data-testid="restore-btn">
                        <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Restore
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className={`rounded-full h-8 ${selectedSenderRule ? 'text-[#A3503B] border-[#E7C5BC]' : ''}`}
                        onClick={() => selectedSenderRule ? setSenderRulesOpen(true) : setSkipSenderOpen(true)}
                        data-testid="btn-skip-sender"
                      >
                        {selectedSenderRule ? <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> : <Ban className="w-3.5 h-3.5 mr-1.5" />}
                        {selectedSenderRule ? 'Sender rule active' : 'Always skip sender'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-hidden">
              <Tabs
                value={previewTab}
                onValueChange={value => {
                  setPreviewTab(value);
                  if (value !== 'draft') setIsDraggingFiles(false);
                }}
                className="h-full flex flex-col"
              >
                <div className="border-b border-border px-5">
                  <TabsList className="bg-transparent h-11 gap-1">
                    <TabsTrigger value="original" className="rounded-full data-[state=active]:bg-[#E0F2F5] data-[state=active]:text-primary data-[state=active]:shadow-none" data-testid="original-email-tab">Original Email</TabsTrigger>
                    <TabsTrigger value="draft" className="rounded-full data-[state=active]:bg-[#E0F2F5] data-[state=active]:text-primary data-[state=active]:shadow-none" data-testid="ai-reply-draft-tab">AI Reply Draft</TabsTrigger>
                    <TabsTrigger value="preview" className="rounded-full data-[state=active]:bg-[#E0F2F5] data-[state=active]:text-primary data-[state=active]:shadow-none" data-testid="html-preview-tab">HTML Preview</TabsTrigger>
                  </TabsList>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {/* AI Draft Tab */}
                  <TabsContent value="draft" className="p-5 m-0 h-full">
                    {selectedEmail.no_reply_needed ? (
                      <div className="bg-muted/50 rounded-2xl p-8 text-center">
                        <SkipForward className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground mb-1" data-testid="triage-detail-message">
                          {selectedEmail.actionability === 'review_only' ? 'This email needs human review before drafting a reply.' : 'No email reply is needed.'}
                        </p>
                        {selectedEmail.actionability_reason && <p className="text-xs text-muted-foreground mb-4" data-testid="triage-detail-reason">{selectedEmail.actionability_reason}</p>}
                        <Button
                          size="sm"
                          onClick={handleRegenerate}
                          disabled={loading}
                          className="bg-primary hover:bg-accent text-primary-foreground rounded-full"
                          data-testid="force-generate-btn"
                        >
                          {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1.5" />}
                          Generate Draft Anyway
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="bg-card rounded-lg border border-border p-4 shadow-sm" data-testid="reply-composer">
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <label htmlFor="reply-draft-editor" className="text-xs font-semibold uppercase text-muted-foreground">Reply draft</label>
                            <span className="text-xs text-muted-foreground" data-testid="draft-save-status">{editText === selectedEmail.draft_text ? 'Saved' : 'Unsaved changes'}</span>
                          </div>
                          <textarea
                            id="reply-draft-editor"
                            value={editText}
                            onChange={event => setEditText(event.target.value)}
                            className="w-full min-h-64 p-4 border border-border rounded-lg text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary bg-white leading-relaxed"
                            placeholder="Write your reply..."
                            disabled={selectedEmail.status === 'pending_send'}
                            data-testid="draft-editor"
                          />
                          <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
                            <p className="text-xs text-muted-foreground">Edit freely, then save or send your latest text.</p>
                            <Button
                              size="sm"
                              onClick={handleSaveDraft}
                              disabled={savingDraft || selectedEmail.status === 'pending_send' || !editText.trim() || editText === selectedEmail.draft_text}
                              className="bg-primary hover:bg-accent text-primary-foreground rounded-full"
                              data-testid="save-draft-button"
                            >
                              {savingDraft ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Edit3 className="w-4 h-4 mr-1.5" />}
                              {savingDraft ? 'Saving...' : 'Save draft'}
                            </Button>
                          </div>
                        </div>

                        <div className="border border-border rounded-lg p-4 bg-white" data-testid="attachment-tray">
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div>
                              <h4 className="text-sm font-semibold text-foreground flex items-center gap-2"><Paperclip className="w-4 h-4 text-primary" /> Attachments</h4>
                              <p className="text-xs text-muted-foreground mt-1">PDF, Word, Excel, JPG or PNG. Up to 10 MB each.</p>
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="rounded-full"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={uploadingAttachments || selectedEmail.status === 'pending_send' || attachments.length >= 10}
                              data-testid="add-reply-attachment-inline-button"
                            >
                              {uploadingAttachments ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Paperclip className="w-4 h-4 mr-1.5" />}
                              {uploadingAttachments ? 'Uploading...' : 'Add files'}
                            </Button>
                          </div>
                          {attachments.length > 0 && (
                            <div className="mt-3 grid gap-2" data-testid="reply-attachment-list">
                              {attachments.map(attachment => (
                                <div key={attachment.id} className="flex items-center gap-3 border border-border rounded-lg px-3 py-2 bg-[#F7FAF9]" data-testid={`reply-attachment-${attachment.id}`}>
                                  <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium truncate" title={attachment.filename}>{attachment.filename}</p>
                                    <p className="text-xs text-muted-foreground">{formatFileSize(attachment.size)}</p>
                                  </div>
                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8 text-[#A3503B] hover:text-[#843F30]"
                                    onClick={() => handleRemoveAttachment(attachment.id)}
                                    data-testid={`remove-reply-attachment-${attachment.id}`}
                                    aria-label={`Remove ${attachment.filename}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Refine with AI */}
                        {selectedEmail.draft_text && (
                          <div className="bg-[#E0F2F5]/40 rounded-2xl border border-[#CDEAEF] p-4" data-testid="refine-panel">
                            <div className="flex items-center gap-2 mb-3">
                              <Sparkles className="w-4 h-4 text-primary" />
                              <h4 className="text-sm font-semibold text-foreground">Refine with AI</h4>
                            </div>

                            {selectedEmail.refine_history && selectedEmail.refine_history.filter(h => h.role === 'user').length > 0 && (
                              <div className="space-y-2 mb-3 max-h-44 overflow-y-auto" data-testid="refine-history">
                                {selectedEmail.refine_history.filter(h => h.role === 'user').map((h, i) => (
                                  <div key={i} className="flex items-start gap-2 text-sm">
                                    <span className="flex-shrink-0 mt-0.5 inline-flex items-center justify-center w-8 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold">You</span>
                                    <p className="text-foreground/80 bg-white rounded-xl px-3 py-1.5 border border-[#CDEAEF] flex-1">{h.content}</p>
                                  </div>
                                ))}
                              </div>
                            )}

                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={refineInput}
                                onChange={e => setRefineInput(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter' && refineInput.trim() && !refining) handleRefine(); }}
                                placeholder=""
                                disabled={refining || selectedEmail.status === 'pending_send' || !editText.trim()}
                                className="flex-1 text-sm border border-border rounded-full px-4 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                                data-testid="refine-input"
                              />
                              <Button
                                size="sm"
                                onClick={handleRefine}
                                disabled={refining || !refineInput.trim()}
                                className="bg-primary hover:bg-accent text-primary-foreground rounded-full"
                                data-testid="refine-send-btn"
                              >
                                {refining ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                <span className="ml-1.5">{refining ? 'Refining...' : 'Refine'}</span>
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </TabsContent>

                  {/* Original Email Tab */}
                  <TabsContent value="original" className="p-5 m-0">
                    <div className="bg-card rounded-2xl border border-border p-6 shadow-sm">
                      <div className="prose prose-sm max-w-none whitespace-pre-wrap text-foreground/85 leading-relaxed">
                        {selectedEmail.body_text || '(empty body)'}
                      </div>
                    </div>
                  </TabsContent>

                  {/* HTML Preview Tab */}
                  <TabsContent value="preview" className="p-5 m-0">
                    {selectedEmail.draft_html ? (
                      <div className="rounded-2xl border border-border overflow-hidden shadow-sm bg-white">
                        <iframe
                          srcDoc={selectedEmail.draft_html}
                          title="Email Preview"
                          className="w-full h-[500px] border-0"
                          sandbox=""
                          data-testid="draft-preview-iframe"
                        />
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-10">No HTML preview available</p>
                    )}
                  </TabsContent>
                </div>
              </Tabs>
            </div>

            {/* Action Bar */}
            {previewTab === 'draft' && ['new', 'drafted', 'flagged'].includes(selectedEmail.status) && (
              <div className="relative z-[70] p-3 sm:p-4 border-t border-border bg-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="reply-action-bar">
                <div className="flex gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                    onClick={handleRegenerate}
                    disabled={loading}
                    data-testid="regenerate-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1.5" />}
                    Regenerate
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={handleFlag} data-testid="flag-btn">
                    <Flag className="w-4 h-4 mr-1.5" /> Flag
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={handleSkip} data-testid={selectedEmail.status === 'skipped' ? 'restore-btn' : 'skip-btn'}>
                    {selectedEmail.status === 'skipped' ? <RotateCcw className="w-4 h-4 mr-1.5" /> : <SkipForward className="w-4 h-4 mr-1.5" />}
                    {selectedEmail.status === 'skipped' ? 'Restore' : 'Skip'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className={`rounded-full ${selectedSenderRule ? 'text-[#A3503B] border-[#E7C5BC]' : ''}`}
                    onClick={() => selectedSenderRule ? setSenderRulesOpen(true) : setSkipSenderOpen(true)}
                    data-testid="btn-skip-sender"
                  >
                    {selectedSenderRule ? <ShieldCheck className="w-4 h-4 mr-1.5" /> : <Ban className="w-4 h-4 mr-1.5" />}
                    {selectedSenderRule ? 'Sender rule active' : 'Always skip sender'}
                  </Button>
                </div>
                <div className="flex items-center gap-3 flex-wrap w-full sm:w-auto justify-between sm:justify-end">
                  {/* Signature Selector */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">Sign as:</span>
                    <select
                      value={selectedSignature}
                      onChange={e => {
                        const newSig = e.target.value;
                        setSelectedSignature(newSig);
                        if (newSig !== 'custom') {
                          setCustomName('');
                          handleApplySignature(newSig, '');
                        }
                      }}
                      className="text-sm border border-border rounded-full px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                      data-testid="signature-selector"
                    >
                      {signatures.map(sig => (
                        <option key={sig.id} value={sig.id}>{sig.label}</option>
                      ))}
                    </select>
                    {selectedSignature === 'custom' && (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={customName}
                          onChange={e => setCustomName(e.target.value)}
                          placeholder="Enter name..."
                          className="text-sm border border-border rounded-full px-3 py-1.5 bg-white w-36 focus:outline-none focus:ring-2 focus:ring-primary"
                          data-testid="custom-name-input"
                          onKeyDown={e => { if (e.key === 'Enter' && customName.trim()) handleApplySignature('custom', customName); }}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { if (customName.trim()) handleApplySignature('custom', customName); }}
                          disabled={!customName.trim()}
                          className="text-xs h-8 rounded-full"
                        >
                          Apply
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept={ACCEPTED_ATTACHMENT_TYPES}
                      onChange={handleAttachmentUpload}
                      className="hidden"
                      data-testid="reply-attachment-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-full"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingAttachments || attachments.length >= 10 || selectedEmail.no_reply_needed}
                      data-testid="add-reply-attachment-button"
                      aria-label="Attach files to this reply"
                    >
                      {uploadingAttachments ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Paperclip className="w-4 h-4 mr-1.5" />}
                      {uploadingAttachments ? 'Uploading...' : attachments.length > 0 ? `Files (${attachments.length})` : 'Attach files'}
                    </Button>
                    <div>
                      <Button
                        type="button"
                        variant="outline"
                        className="relative z-[80] rounded-full"
                        data-testid="schedule-reply-btn"
                        aria-expanded={scheduleOpen}
                        onPointerDown={event => {
                          event.preventDefault();
                          event.stopPropagation();
                          setScheduleOpen(true);
                        }}
                        onClick={() => setScheduleOpen(true)}
                        disabled={sending || !editText.trim() || selectedEmail.no_reply_needed || (selectedSignature === 'custom' && !customName.trim())}
                      >
                        <CalendarClock className="w-4 h-4 mr-1.5" /> Schedule
                      </Button>
                      {scheduleOpen && (
                        <div
                          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/25 p-4"
                          data-testid="schedule-reply-popover"
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby="schedule-reply-title"
                          onMouseDown={event => { if (event.target === event.currentTarget) setScheduleOpen(false); }}
                        >
                          <div className="w-full max-w-sm rounded-lg border border-border bg-white p-5 shadow-xl">
                            <div className="flex items-start justify-between gap-3 mb-1">
                              <p id="schedule-reply-title" className="text-sm font-semibold text-foreground">Schedule this reply</p>
                              <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={() => setScheduleOpen(false)}
                                data-testid="close-schedule-reply-button"
                                aria-label="Close schedule dialog"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                            <p className="text-xs text-muted-foreground mb-3">Sends automatically at your chosen time, even if the app is closed.</p>
                            <input
                              type="datetime-local"
                              value={scheduleAt}
                              min={toLocalInputValue(new Date())}
                              onChange={e => setScheduleAt(e.target.value)}
                              data-testid="schedule-reply-datetime"
                              className="w-full border border-border rounded-lg px-3 py-2 text-sm mb-3"
                            />
                            <Button
                              size="sm"
                              className="w-full rounded-full bg-primary text-primary-foreground hover:bg-accent"
                              disabled={!scheduleAt || sending}
                              onClick={() => handleSendDraft(new Date(scheduleAt).toISOString())}
                              data-testid="confirm-schedule-reply-btn"
                            >
                              {sending ? 'Scheduling...' : 'Schedule send'}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={() => handleSendDraft()}
                      disabled={sending || !editText.trim() || selectedEmail.no_reply_needed || (selectedSignature === 'custom' && !customName.trim())}
                      data-testid="send-draft-btn"
                      className="bg-primary hover:bg-accent text-primary-foreground rounded-full shadow-sm px-6"
                    >
                      {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                      {sending ? 'Sending...' : 'Approve & Send'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <AlertDialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <AlertDialogContent className="max-w-md rounded-lg" data-testid="bulk-email-action-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">
              {bulkMode === 'restore' ? 'Restore selected emails?' : 'Skip selected emails?'}
            </AlertDialogTitle>
            <AlertDialogDescription data-testid="bulk-email-action-description">
              {bulkMode === 'restore'
                ? `${selectedVisibleIds.length} email${selectedVisibleIds.length === 1 ? '' : 's'} will return to Needs Action.`
                : `${selectedVisibleIds.length} email${selectedVisibleIds.length === 1 ? '' : 's'} will move to Skipped.`}
              {' '}This changes only the Email Hub status. Nothing is deleted, moved, archived, or marked read in Apple Mail or Hostinger.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkWorking} data-testid="cancel-bulk-email-action-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={bulkWorking || selectedVisibleIds.length === 0}
              onClick={event => {
                event.preventDefault();
                runBulkAction(bulkMode, selectedVisibleIds);
              }}
              className="rounded-full bg-primary text-primary-foreground hover:bg-accent"
              data-testid="confirm-bulk-email-action-button"
            >
              {bulkWorking ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : bulkMode === 'restore' ? <RotateCcw className="w-4 h-4 mr-1.5" /> : <SkipForward className="w-4 h-4 mr-1.5" />}
              {bulkMode === 'restore' ? 'Restore selected' : 'Skip selected'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SenderRulesDialog
        open={senderRulesOpen}
        onOpenChange={setSenderRulesOpen}
        rules={senderRules}
        loading={senderRulesLoading}
        onRemove={handleRemoveSenderRule}
      />
      <SkipSenderDialog
        open={skipSenderOpen}
        onOpenChange={setSkipSenderOpen}
        email={selectedEmail}
        working={skipSenderWorking}
        onConfirm={handleCreateSenderRule}
      />
    </div>
  );
};

export default InboxAssistant;
